/**
 * Tournament Builder — creation wizard
 * Route: /prioritization/tournaments/new
 *
 * 3-step flow:
 *   Step 1 — Structure: name, round count, tool, advancement rule
 *   Step 2 — Items: select items from the item library
 *   Step 3 — Review + Create
 */

import { useState, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowRight,
  ArrowLeft,
  Trophy,
  Check,
  Search,
  ChevronRight,
  AlertCircle,
  Users,
} from 'lucide-react';
import { TournamentParticipantEditor } from '../../../components/prioritization/tournaments/TournamentParticipantEditor';
import type { ParticipantMode } from '../../../types/prioritization/tournament';
import { useTournamentStore } from '../../../stores/prioritization/tournamentStore';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import {
  RANKING_TOOLS,
  DEFAULT_ROUND_LABELS,
  createTournamentWithRounds,
  type RoundConfig,
} from '../../../lib/tournamentSetup';

// The tool catalog, round names and the create call live in src/lib/tournamentSetup.ts,
// shared with the cycle configure wizard's Tournament step.

// ─── Bracket preview ──────────────────────────────────────────────────────────

function BracketPreview({
  totalItems,
  rounds,
}: {
  totalItems: number;
  rounds: RoundConfig[];
}) {
  let currentCount = totalItems;
  return (
    <div className="flex items-center gap-1 flex-wrap">
      <span className="text-xs font-semibold text-[var(--text-sec)] bg-[var(--slate-dim)] border border-[var(--slate)] px-2 py-1 rounded">
        {currentCount} items
      </span>
      {rounds.map((round, i) => {
        const advances = Math.min(round.advancingCount, currentCount);
        currentCount = advances;
        return (
          <div key={i} className="flex items-center gap-1">
            <ChevronRight className="h-3 w-3 text-[var(--text-ter)]" />
            <div className="text-center">
              <div className="text-[10px] text-[var(--text-ter)] leading-none mb-0.5">{round.label || `Round ${i + 1}`}</div>
              <span className={`text-xs font-semibold px-2 py-1 rounded border ${
                i === rounds.length - 1
                  ? 'bg-[var(--mustard-dim)] border-[var(--mustard)] text-[var(--mustard)]'
                  : 'bg-[var(--surface)] border-[var(--rule)] text-[var(--text-sec)]'
              }`}>
                top {advances}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Step 1: Structure ────────────────────────────────────────────────────────

function StepStructure({
  name, setName,
  description, setDescription,
  roundCount, setRoundCount,
  rounds, setRounds,
  toolId, setToolId,
  totalItems,
  onNext,
}: {
  name: string; setName: (v: string) => void;
  description: string; setDescription: (v: string) => void;
  roundCount: number; setRoundCount: (v: number) => void;
  rounds: RoundConfig[]; setRounds: (v: RoundConfig[]) => void;
  toolId: string; setToolId: (v: string) => void;
  totalItems: number;
  onNext: () => void;
}) {
  const canContinue = name.trim().length > 0 && toolId.length > 0;

  const handleRoundCountChange = (count: number) => {
    setRoundCount(count);
    const labels = DEFAULT_ROUND_LABELS[count] ?? Array.from({ length: count }, (_, i) => `Round ${i + 1}`);
    const existing = rounds;
    const newRounds: RoundConfig[] = labels.map((label, i) => ({
      label,
      advancingCount: existing[i]?.advancingCount ?? Math.max(1, Math.ceil(totalItems / Math.pow(2, i + 1))),
    }));
    setRounds(newRounds);
  };

  const updateRound = (i: number, updates: Partial<RoundConfig>) => {
    setRounds(rounds.map((r, idx) => idx === i ? { ...r, ...updates } : r));
  };

  return (
    <div className="space-y-6">
      {/* Name */}
      <div>
        <label className="block text-sm font-medium text-[var(--text-sec)] mb-1.5">
          Tournament name <span className="text-[var(--coral)]">*</span>
        </label>
        <input
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="e.g. Q2 Planning Tournament"
          className="w-full px-3 py-2 text-sm border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-blue-500/20"
        />
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-[var(--text-sec)] mb-1.5">Description</label>
        <textarea
          value={description}
          onChange={e => setDescription(e.target.value)}
          placeholder="What is this tournament deciding? Who are the participants?"
          rows={2}
          className="w-full px-3 py-2 text-sm border border-[var(--rule)] resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/20"
        />
      </div>

      {/* Round count */}
      <div>
        <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">Number of rounds</label>
        <div className="flex gap-2">
          {[2, 3, 4, 5].map(n => (
            <button
              key={n}
              onClick={() => handleRoundCountChange(n)}
              className={`flex-1 py-2.5 text-sm font-medium  border-2 transition-colors ${
                roundCount === n
                  ? 'border-[var(--slate)] bg-[var(--slate-dim)] text-[var(--text-sec)]'
                  : 'border-[var(--rule)] bg-[var(--bg-raised)] text-[var(--text-sec)] hover:border-[var(--rule)]'
              }`}
            >
              {n}
            </button>
          ))}
        </div>
      </div>

      {/* Round labels + advancement */}
      <div>
        <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">Configure rounds</label>
        <div className="space-y-2">
          {rounds.map((round, i) => (
            <div key={i} className="flex flex-wrap items-center gap-x-3 gap-y-1 bg-[var(--surface)] border border-[var(--rule)] px-3 py-2.5">
              <span className="text-xs text-[var(--text-ter)] w-6 text-right shrink-0">{i + 1}</span>
              <input
                value={round.label}
                onChange={e => updateRound(i, { label: e.target.value })}
                placeholder={`Round ${i + 1}`}
                className="flex-1 min-w-[8rem] text-sm bg-transparent border-none focus:outline-none text-[var(--text-sec)]"
              />
              <div className="flex items-center gap-1.5 shrink-0">
                <span className="text-xs text-[var(--text-ter)]">top</span>
                <input
                  type="number"
                  min={1}
                  value={round.advancingCount}
                  onChange={e => updateRound(i, { advancingCount: Math.max(1, parseInt(e.target.value) || 1) })}
                  className="w-12 text-sm text-center border border-[var(--rule)] px-1 py-0.5 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                />
                <span className="text-xs text-[var(--text-ter)]">advance</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Tool */}
      <div>
        <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">
          Ranking tool <span className="text-xs text-[var(--text-ter)] font-normal">(used for all rounds)</span>
        </label>
        <div className="grid grid-cols-1 gap-1.5">
          {RANKING_TOOLS.map(tool => (
            <button
              key={tool.id}
              onClick={() => setToolId(tool.id)}
              className={`flex items-center justify-between text-left px-3 py-2.5  border transition-colors ${
                toolId === tool.id
                  ? 'border-[var(--slate)] bg-[var(--slate-dim)]'
                  : 'border-[var(--rule)] bg-[var(--bg-raised)] hover:border-[var(--rule)] hover:bg-[var(--surface)]'
              }`}
            >
              <div>
                <span className="text-sm font-medium text-[var(--text-pri)]">{tool.label}</span>
                <span className="text-xs text-[var(--text-ter)] ml-2">{tool.desc}</span>
              </div>
              {toolId === tool.id && <Check className="h-4 w-4 text-[var(--text-ter)] shrink-0" />}
            </button>
          ))}
        </div>
      </div>

      {/* Preview */}
      {totalItems > 0 && (
        <div className="bg-[var(--surface)] border border-[var(--rule)] p-3">
          <p className="text-xs text-[var(--text-ter)] mb-2 font-medium">Bracket preview</p>
          <BracketPreview totalItems={totalItems} rounds={rounds} />
        </div>
      )}

      <div className="flex justify-end pt-2">
        <button
          onClick={onNext}
          disabled={!canContinue}
          className={`flex items-center gap-2 px-5 py-2.5 text-sm font-medium  transition-colors ${
            canContinue
              ? 'bg-[var(--slate)] hover:bg-[var(--slate)] text-white'
              : 'bg-[var(--hover-tint)] text-[var(--text-ter)] cursor-not-allowed'
          }`}
        >
          Next: Select items <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

// ─── Step 2: Items ────────────────────────────────────────────────────────────

const ELIGIBLE_STATES = new Set(['TRIAGED', 'DIRECT', 'SHAPING', 'SHAPED', 'VALUES_PROPOSED', 'RECONCILED', 'PRIORITIZED']);

function StepItems({
  selectedIds, setSelectedIds, onBack, onNext, rounds,
}: {
  selectedIds: string[];
  setSelectedIds: (ids: string[]) => void;
  onBack: () => void;
  onNext: () => void;
  rounds: RoundConfig[];
}) {
  const [search, setSearch] = useState('');
  // Select the raw items array (stable reference) then filter in useMemo —
  // avoids creating a new array inside the Zustand selector on every render.
  const rawItems = useItemsStore(s => s.items);
  const allItems = useMemo(
    () => rawItems.filter(c => c.lifecycle_state === 'item'),
    [rawItems]
  );

  const eligible = useMemo(() => {
    return allItems.filter(item =>
      !item.detailedState || ELIGIBLE_STATES.has(item.detailedState)
    );
  }, [allItems]);

  const filtered = useMemo(() => {
    if (!search) return eligible;
    const q = search.toLowerCase();
    return eligible.filter(item =>
      item.title.toLowerCase().includes(q) ||
      item.itemCategory?.toLowerCase().includes(q)
    );
  }, [eligible, search]);

  const toggleItem = useCallback((id: string) => {
    setSelectedIds(
      selectedIds.includes(id)
        ? selectedIds.filter(x => x !== id)
        : [...selectedIds, id]
    );
  }, [selectedIds, setSelectedIds]);

  const canContinue = selectedIds.length >= 2;

  return (
    <div className="space-y-4">
      <div>
        <p className="text-sm text-[var(--text-sec)] mb-3">
          Select the items that will compete in this tournament. Choose at least 2.
        </p>
        {selectedIds.length > 0 && (
          <div className="flex items-center gap-2 mb-3">
            <BracketPreview totalItems={selectedIds.length} rounds={rounds} />
          </div>
        )}
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-[var(--text-ter)]" />
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search items…"
          className="w-full pl-9 pr-3 py-2 text-sm border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-blue-500/20"
        />
      </div>

      <div className="border border-[var(--rule)] divide-y divide-[var(--rule)] max-h-80 overflow-y-auto">
        {filtered.length === 0 && (
          <div className="py-10 text-center text-sm text-[var(--text-ter)]">
            {eligible.length === 0
              ? 'No eligible items found. Items must be in Triage or later stages.'
              : 'No items match your search.'}
          </div>
        )}
        {filtered.map(item => {
          const selected = selectedIds.includes(item.id);
          return (
            <button
              key={item.id}
              onClick={() => toggleItem(item.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                selected ? 'bg-[var(--slate-dim)]' : 'hover:bg-[var(--surface)]'
              }`}
            >
              <div className={`h-4 w-4 shrink-0 rounded border-2 flex items-center justify-center transition-colors ${
                selected ? 'border-[var(--slate)] bg-[var(--slate)]' : 'border-[var(--rule)]'
              }`}>
                {selected && <Check className="h-2.5 w-2.5 text-white" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-[var(--text-pri)] truncate">{item.title}</p>
                <p className="text-xs text-[var(--text-ter)] mt-0.5">
                  {item.itemCategory && <span className="capitalize">{item.itemCategory}</span>}
                  {item.detailedState && <span className="ml-1.5 text-[var(--text-ter)]">· {item.detailedState}</span>}
                </p>
              </div>
            </button>
          );
        })}
      </div>

      <div className="flex items-center justify-between pt-2">
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-sm text-[var(--text-ter)] hover:text-[var(--text-sec)] transition-colors"
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <div className="flex items-center gap-3">
          <span className="text-xs text-[var(--text-ter)]">
            {selectedIds.length} item{selectedIds.length !== 1 ? 's' : ''} selected
          </span>
          <button
            onClick={onNext}
            disabled={!canContinue}
            className={`flex items-center gap-2 px-5 py-2.5 text-sm font-medium  transition-colors ${
              canContinue
                ? 'bg-[var(--slate)] hover:bg-[var(--slate)] text-white'
                : 'bg-[var(--hover-tint)] text-[var(--text-ter)] cursor-not-allowed'
            }`}
          >
            Review & Create <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Step 3: Review + Create ──────────────────────────────────────────────────

function StepReview({
  name, description, rounds, toolId, selectedIds,
  onBack, onCreate, creating,
}: {
  name: string; description: string;
  rounds: RoundConfig[];
  toolId: string;
  selectedIds: string[];
  onBack: () => void;
  onCreate: () => void;
  creating: boolean;
}) {
  const tool = RANKING_TOOLS.find(t => t.id === toolId);
  const rawItems = useItemsStore(s => s.items);
  const selectedItems = useMemo(
    () => rawItems.filter(c => c.lifecycle_state === 'item' && selectedIds.includes(c.id)),
    [rawItems, selectedIds]
  );

  return (
    <div className="space-y-6">
      <div className="bg-[var(--surface)] border border-[var(--rule)] p-5 space-y-4">
        <div>
          <p className="text-xs text-[var(--text-ter)] font-medium mb-0.5">Tournament name</p>
          <p className="text-base font-semibold text-[var(--text-pri)]">{name}</p>
          {description && <p className="text-sm text-[var(--text-ter)] mt-0.5">{description}</p>}
        </div>

        <div>
          <p className="text-xs text-[var(--text-ter)] font-medium mb-0.5">Ranking tool</p>
          <p className="text-sm text-[var(--text-sec)]">{tool?.label ?? toolId}</p>
        </div>

        <div>
          <p className="text-xs text-[var(--text-ter)] font-medium mb-2">Bracket</p>
          <BracketPreview totalItems={selectedIds.length} rounds={rounds} />
          <div className="mt-3 space-y-1.5">
            {rounds.map((round, i) => (
              <div key={i} className="flex items-center gap-2 text-sm text-[var(--text-sec)]">
                <span className="w-5 h-5 rounded-full bg-[var(--hover-tint)] text-[var(--text-sec)] text-xs flex items-center justify-center shrink-0">
                  {i + 1}
                </span>
                <span className="font-medium">{round.label}</span>
                <span className="text-[var(--text-ter)]">— top {round.advancingCount} advance</span>
              </div>
            ))}
          </div>
        </div>

        <div>
          <p className="text-xs text-[var(--text-ter)] font-medium mb-1">{selectedIds.length} items</p>
          <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
            {selectedItems.map(item => (
              <span key={item.id} className="text-xs bg-[var(--bg-raised)] border border-[var(--rule)] rounded px-2 py-0.5 text-[var(--text-sec)] truncate max-w-[180px]">
                {item.title}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between pt-2">
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-sm text-[var(--text-ter)] hover:text-[var(--text-sec)] transition-colors"
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <button
          onClick={onCreate}
          disabled={creating}
          className="flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white bg-[var(--slate)] hover:bg-[var(--slate)] disabled:opacity-50 transition-colors"
        >
          {creating ? 'Creating…' : 'Create Tournament'}
          {!creating && <Trophy className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
}

// ─── Step 3: Participants ─────────────────────────────────────────────────────

interface StepParticipantsProps {
  participantMode: ParticipantMode;
  setParticipantMode: (m: ParticipantMode) => void;
  defaultParticipantIds: string[];
  setDefaultParticipantIds: (ids: string[]) => void;
  perRoundParticipants: string[][];
  setPerRoundParticipants: (ids: string[][]) => void;
  rounds: RoundConfig[];
  onBack: () => void;
  onNext: () => void;
}

function StepParticipants({
  participantMode,
  setParticipantMode,
  defaultParticipantIds,
  setDefaultParticipantIds,
  perRoundParticipants,
  setPerRoundParticipants,
  rounds,
  onBack,
  onNext,
}: StepParticipantsProps) {
  const roundConfigs = rounds.map((r, i) => ({
    roundNumber: i + 1,
    label: r.label || `Round ${i + 1}`,
    participantIds: perRoundParticipants[i] ?? [],
  }));

  const handleRoundChange = (roundNumber: number, ids: string[]) => {
    const next = [...perRoundParticipants];
    next[roundNumber - 1] = ids;
    setPerRoundParticipants(next);
  };

  return (
    <div className="space-y-6">
      <div className="border border-[var(--rule)] bg-[var(--bg-raised)] p-5">
        <div className="flex items-center gap-2 mb-4">
          <Users className="h-4 w-4 text-[var(--text-ter)]" />
          <h3 className="text-sm font-semibold text-[var(--text-pri)]">Participants</h3>
        </div>
        <p className="text-xs text-[var(--text-ter)] mb-4">
          Choose who participates in sessions. You can change this later on the tournament detail page.
        </p>
        <TournamentParticipantEditor
          mode={participantMode}
          defaultParticipantIds={defaultParticipantIds}
          roundConfigs={roundConfigs}
          onModeChange={setParticipantMode}
          onDefaultParticipantsChange={setDefaultParticipantIds}
          onRoundParticipantsChange={handleRoundChange}
        />
      </div>

      <div className="flex items-center justify-between pt-2">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1.5 text-sm text-[var(--text-ter)] hover:text-[var(--text-sec)] transition-colors"
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <button
          type="button"
          onClick={onNext}
          className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 transition-colors"
        >
          Review <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

// ─── Main wizard ──────────────────────────────────────────────────────────────

const STEPS = ['Structure', 'Items', 'Participants', 'Review'];

export function TournamentBuilder() {
  const navigate       = useNavigate();
  const [searchParams] = useSearchParams();
  const userId         = useUserStore(s => s.userId);
  const create         = useTournamentStore(s => s.createTournament);
  const addRound       = useTournamentStore(s => s.addRound);

  // Context passed in from Track / Cycle surfaces
  const contextTrackId = searchParams.get('trackId') ?? undefined;
  const contextCycleId = searchParams.get('cycleId') ?? undefined;

  // Back destination: return to track defaults, cycle tournaments tab, or list
  const backTo = contextTrackId
    ? `/prioritization/tracks/${contextTrackId}?tab=defaults`
    : contextCycleId
      ? `/prioritization/cycles/${contextCycleId}?tab=tournaments`
      : '/prioritization/tournaments';

  const [step, setStep]               = useState(0);
  const [creating, setCreating]       = useState(false);
  const [error, setError]             = useState<string | null>(null);

  // Step 1 state
  const [name, setName]               = useState('');
  const [description, setDescription] = useState('');
  const [roundCount, setRoundCount]   = useState(3);
  const [rounds, setRounds]           = useState<RoundConfig[]>(
    DEFAULT_ROUND_LABELS[3].map((label, i) => ({ label, advancingCount: Math.max(1, 4 - i) }))
  );
  const [toolId, setToolId]           = useState('stack_ranking');

  // Step 2 state
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  // Step 3 state
  const [participantMode, setParticipantMode]                   = useState<ParticipantMode>('open');
  const [defaultParticipantIds, setDefaultParticipantIds]       = useState<string[]>([]);
  const [perRoundParticipants, setPerRoundParticipants]         = useState<string[][]>(
    rounds.map(() => [])
  );

  // Keep per-round participant array in sync with round count changes
  const syncedPerRound = useMemo(() => {
    const current = perRoundParticipants;
    if (current.length === rounds.length) return current;
    return rounds.map((_, i) => current[i] ?? []);
  }, [rounds, perRoundParticipants]);

  const handleCreate = useCallback(async () => {
    if (!userId) return;
    setCreating(true);
    setError(null);
    try {
      // The one create path (src/lib/tournamentSetup.ts): a DRAFT tournament and its rounds.
      const tournament = await createTournamentWithRounds(
        {
          name,
          description,
          trackId: contextTrackId,
          cycleId: contextCycleId,
          facilitatorId: userId,
          itemIds: selectedIds,
          toolId,
          rounds,
          participantMode,
          defaultParticipantIds,
          perRoundParticipantIds: syncedPerRound,
        },
        { createTournament: create, addRound },
      );

      navigate(`/prioritization/tournaments/${tournament.id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create tournament');
    } finally {
      setCreating(false);
    }
  }, [
    userId, name, description, rounds, toolId, selectedIds,
    participantMode, defaultParticipantIds, syncedPerRound,
    contextTrackId, contextCycleId,
    create, addRound, navigate,
  ]);

  const contextLabel = contextTrackId
    ? 'Track'
    : contextCycleId
      ? 'Cycle'
      : null;

  return (
    <div className="max-w-2xl mx-auto py-8 px-6">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate(backTo)}
          className="text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)] flex items-center gap-1 mb-2"
        >
          <ArrowLeft className="h-3 w-3" />
          {contextLabel ? `Back to ${contextLabel}` : 'Tournaments'}
        </button>
        <h1 className="text-2xl font-bold text-[var(--text-pri)] flex items-center gap-2">
          <Trophy className="h-6 w-6 text-[var(--text-sec)]" />
          New Tournament
          {contextLabel && (
            <span className="text-base font-normal text-[var(--text-ter)] ml-1">
              for this {contextLabel}
            </span>
          )}
        </h1>
        <p className="text-sm text-[var(--text-ter)] mt-1">
          Set up a multi-round bracket to narrow your item pool through successive ranked sessions
        </p>
      </div>

      {/* Step indicator */}
      {/* On a phone only the current step keeps its name; the rest are numbers, so all four fit */}
      <div className="flex items-center gap-0 mb-8">
        {STEPS.map((s, i) => (
          <div key={s} className="flex items-center">
            <div className={`flex items-center gap-2 px-2 sm:px-3 py-1.5  text-xs font-medium transition-colors ${
              i === step
                ? 'bg-[var(--slate-dim)] text-[var(--text-sec)]'
                : i < step
                  ? 'text-[var(--green)] dark:text-[var(--text-ter)]'
                  : 'text-[var(--text-ter)]'
            }`}>
              {i < step
                ? <Check className="h-3.5 w-3.5" />
                : <span className="w-3.5 h-3.5 rounded-full border border-current flex items-center justify-center text-[10px]">{i + 1}</span>
              }
              <span className={i === step ? '' : 'max-sm:sr-only'}>{s}</span>
            </div>
            {i < STEPS.length - 1 && (
              <ChevronRight className="h-3.5 w-3.5 text-[var(--text-ter)] mx-1" />
            )}
          </div>
        ))}
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 bg-[var(--coral-dim)] border border-[var(--coral)] px-4 py-3 mb-6 text-sm text-[var(--coral)]">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {error}
        </div>
      )}

      {/* Step content */}
      {step === 0 && (
        <StepStructure
          name={name} setName={setName}
          description={description} setDescription={setDescription}
          roundCount={roundCount} setRoundCount={setRoundCount}
          rounds={rounds} setRounds={setRounds}
          toolId={toolId} setToolId={setToolId}
          totalItems={selectedIds.length}
          onNext={() => setStep(1)}
        />
      )}
      {step === 1 && (
        <StepItems
          selectedIds={selectedIds}
          setSelectedIds={setSelectedIds}
          rounds={rounds}
          onBack={() => setStep(0)}
          onNext={() => setStep(2)}
        />
      )}
      {step === 2 && (
        <StepParticipants
          participantMode={participantMode}
          setParticipantMode={setParticipantMode}
          defaultParticipantIds={defaultParticipantIds}
          setDefaultParticipantIds={setDefaultParticipantIds}
          perRoundParticipants={syncedPerRound}
          setPerRoundParticipants={setPerRoundParticipants}
          rounds={rounds}
          onBack={() => setStep(1)}
          onNext={() => setStep(3)}
        />
      )}
      {step === 3 && (
        <StepReview
          name={name}
          description={description}
          rounds={rounds}
          toolId={toolId}
          selectedIds={selectedIds}
          onBack={() => setStep(2)}
          onCreate={handleCreate}
          creating={creating}
        />
      )}
    </div>
  );
}

export default TournamentBuilder;
